import {Grid} from 'src/design/atoms/Grid';
import {Group} from 'src/design/atoms/Group';
import {Paper} from 'src/design/atoms/Paper';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import {Title} from 'src/design/atoms/Title';
import {IconLock} from '@tabler/icons-react';
import React from 'react';
import type {
  NotificationVariable} from 'src/api/ProactApiClient';
import {
  NotificationVariableTypeEnum,
} from 'src/api/ProactApiClient';
import {CopyActionIcon} from 'src/design/molecules/CopyActionIcon';
import type {PhaseVariableGroup} from 'src/app/workflows/phaseVariables';

export interface NotificationAttributeGridProps {
  variables: NotificationVariable[];
  allowCopyToClipboard: boolean;
  phaseVariableGroups?: PhaseVariableGroup[];
}
export const NotificationAttributeGrid: React.FC<
  NotificationAttributeGridProps
> = ({variables, allowCopyToClipboard, phaseVariableGroups}) => {
  const sysVariables = variables.filter(
    i => i.type === NotificationVariableTypeEnum.System
  );
  const modVariables = variables.filter(
    i => i.type === NotificationVariableTypeEnum.Module
  );
  const anySys = sysVariables.length > 0;
  const anyMod = modVariables.length > 0;

  const renderGrid = (
    title: string,
    description: string,
    items: NotificationVariable[]
  ) => (
    <Stack gap="sm">
      <div>
        <Title order={5}>{title}</Title>
        <Text size="sm" c="dimmed">
          {description}
        </Text>
      </div>
      <Grid align="stretch" gutter="sm">
        {items.map(variable => {
          const isUnavailable = variable.unavailable === true;
          const reason = variable.unavailableReason;
          return (
            <Grid.Col key={variable.variable} span={{base: 12, sm: 6, xl: 3}}>
              <Paper
                p="sm"
                h="100%"
                bg="var(--mantine-color-gray-0)"
                style={isUnavailable ? {opacity: 0.6} : undefined}
              >
                <Stack gap="xs" h="100%" justify="space-between">
                  <div>
                    <Group gap="xs" mb={4}>
                      {!isUnavailable && allowCopyToClipboard && (
                        <CopyActionIcon value={`{{${variable.variable}}}`} />
                      )}
                      {isUnavailable && (
                        <IconLock
                          size={14}
                          color="var(--mantine-color-yellow-8)"
                        />
                      )}
                      <Text
                        size="sm"
                        fw={600}
                        style={{fontFamily: 'monospace'}}
                        c={isUnavailable ? 'dimmed' : undefined}
                      >
                        {variable.variable}
                      </Text>
                    </Group>
                    <Text size="xs" c="dimmed">
                      {variable.description}.
                    </Text>
                  </div>
                  {isUnavailable && reason ? (
                    <Text size="xs" c="yellow.8" fs="italic">
                      {reason}
                    </Text>
                  ) : (
                    <Text size="xs" c="dimmed" fs="italic">
                      Example: {variable.example}
                    </Text>
                  )}
                </Stack>
              </Paper>
            </Grid.Col>
          );
        })}
      </Grid>
    </Stack>
  );

  const NoDataText = 'No variables available for this configuration.';

  return (
    <Stack gap="lg">
      {anySys ? (
        renderGrid(
          'System Variables',
          'Shared across all notifications.',
          sysVariables
        )
      ) : (
        <div>
          <Title order={5}>System Variables</Title>
          <Text size="sm" c="dimmed">
            {NoDataText}
          </Text>
        </div>
      )}
      {anyMod ? (
        renderGrid(
          'Module Variables',
          'Specific to your selected module and configuration.',
          modVariables
        )
      ) : (
        <div>
          <Title order={5}>Module Variables</Title>
          <Text size="sm" c="dimmed">
            {NoDataText}
          </Text>
        </div>
      )}
      {phaseVariableGroups && phaseVariableGroups.length > 0 && (
        <>
          {phaseVariableGroups.map(group => (
            renderGrid(
              `Phase Variables — ${group.phaseName} (${group.phaseSlug})`,
              `Variables from actions in the ${group.phaseName} phase.`,
              group.variables
            )
          ))}
        </>
      )}
    </Stack>
  );
};
